import {Component, DestroyRef, inject, input, InputSignal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormsModule} from '@angular/forms';
import {
  DsBoxModule,
  DsButtonModule,
  DsCheckboxModule,
  DsFormFieldModule,
  DsLabelModule,
  DsLoadingModule,
  DsMessageModule,
  DsModalModule,
  DsRadioGroupModule,
  DsSelectModule,
  DsTabsModule,
  SelectListOption
} from '@bmw-ds/components';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {
  AddedQuantityStrategy,
  Brand,
  DefaultPartsSearchBrand,
  IServerApi
} from '@service-and-repairs/awpintegrationlib';
import {AnalyticsService} from '../../core/analytics/services/analytics.service';
import {AppsService} from '../../core/apps/services/apps.service';
import {UserSettings} from '../../core/user/interfaces/user-settings';
import {UserData} from '../../core/user/models/user-data';
import {UserMapper} from '../../core/user/models/user-mapper';
import {LanguageService} from '../../core/user/services/language.service';
import {UserService} from '../../core/user/services/user.service';
import {IIframeAppConfiguration} from '../../interfaces/IIframeAppConfiguration';
import {IWebComponentAppConfiguration} from '../../interfaces/IWebComponentAppConfiguration';

import {Locales} from '../../locales';
import {AwpClientLibService} from '../../services/awp-client-lib.service';
import {countries} from './countries';

@Component({
  selector: 'app-user-settings-overlay',
  templateUrl: './user-settings-overlay.component.html',
  styleUrls: ['./user-settings-overlay.component.scss'],
  imports: [
    DsModalModule,
    DsBoxModule,
    DsTabsModule,
    FormsModule,
    DsFormFieldModule,
    DsLabelModule,
    DsSelectModule,
    DsRadioGroupModule,
    DsCheckboxModule,
    DsButtonModule,
    DsLoadingModule,
    DsMessageModule,
    TranslateModule
  ]
})
export class UserSettingsOverlayComponent {
  userService: UserService = inject(UserService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly languageService: LanguageService = inject(LanguageService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly awpClientLibService: AwpClientLibService = inject(AwpClientLibService);
  private readonly appsService: AppsService = inject(AppsService);

  readonly state: InputSignal<{ settingsDialogVisible: boolean }>
    = input<{ settingsDialogVisible: boolean }>();

  userSettings: UserSettings;

  previousDefaultApp: string;
  defaultAppOptions: SelectListOption[] = [];
  defaultPartsSearchBrandOptions: SelectListOption[] = [];
  systemLanguageOptions: SelectListOption[] = [];
  countryOptions: SelectListOption[] = [];
  brandOptions: SelectListOption[] = [];
  eshWriteModeOptions: SelectListOption[] = [];

  awpLib: IServerApi;
  backendCallPending: boolean = false;
  errorMessage: string;

  constructor() {
    this.awpLib = this.awpClientLibService.serverApi;
    this.setDefaultAppOptions();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.setDefaultAppOptions.bind(this));

    Locales.getLocaleIds().forEach((locale: string): void => {
      this.systemLanguageOptions.push({
        label: this.getLabelForUserLocale(locale),
        id: locale
      });
    });

    countries.forEach((country: string): void => {
      this.countryOptions.push({
        label: this.translate.instant('country.' + country),
        id: country
      });
    });

    this.brandOptions = [
      {
        label: 'BMW',
        id: Brand.BMW
      },
      {
        label: 'BMW i',
        id: Brand.BMW_i
      },
      {
        label: 'MINI',
        id: Brand.MINI
      },
      {
        label: 'Rolls-Royce',
        id: Brand.RR
      },
      {
        label: 'BMW Motorrad',
        id: Brand.MOT
      },
      {
        label: 'Zinoro',
        id: Brand.ZINORO
      }
    ];
    this.defaultPartsSearchBrandOptions = [
      {
        label: 'BMW',
        id: DefaultPartsSearchBrand.BMW
      },
      {
        label: 'BMW i',
        id: DefaultPartsSearchBrand.BMW_I
      },
      {
        label: 'MINI',
        id: DefaultPartsSearchBrand.MINI
      },
      {
        label: 'BMW Motorrad',
        id: DefaultPartsSearchBrand.BMW_MOTORRAD
      },
      {
        label: 'Rolls-Royce',
        id: DefaultPartsSearchBrand.ROLLS_ROYCE
      }
    ];

    this.userService.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userData: UserData): void => {
        this.userSettings = UserMapper.toUserSettings(userData);
        this.previousDefaultApp = this.userSettings.defaultApp;
        this.setEshWriteModeOptions();
      });
  }

  static clearSettingsFromLocalStorage(): void {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (!key.toLowerCase().includes('token')) {
        localStorage.removeItem(key);
      }
    }
  }

  private postDefaultAppChangedEvent(): void {
    if (this.userSettings.defaultApp !== this.previousDefaultApp) {
      this.analyticsService.postUserSettingsChangedEvent('defaultApp', this.userSettings.defaultApp);
    }
  }

  saveSettings(): void {
    this.errorMessage = undefined;

    this.backendCallPending = true;
    this.userService.setUserSettings(this.userSettings)
      // ISPI-484765: return the inner promise so .finally() (which reloads the browser) waits for the language
      // change to be applied instead of racing with it.
      .then((): Promise<void> => {
        this.closeDropdown();
        return this.languageService.use(this.userSettings.systemLanguage).then((): void => {
          this.awpLib.notifySuccess(this.translate.instant('user.notificationSaveSuccess'));
        });
      })
      .catch((): Promise<void> => this.languageService.use(this.userSettings.systemLanguage).then((): void => {
        this.errorMessage = this.translate.instant('user.notificationSaveError');
      }))
      .finally((): void => {
        this.backendCallPending = false;
        this.postDefaultAppChangedEvent();
        this.updateAndPublishUser();
        this.reloadBrowser();
      });
  }

  private reloadBrowser(): void {
    window.location.reload();
  }

  deleteSettings(): void {
    this.errorMessage = undefined;
    this.backendCallPending = true;
    this.userService.deletePersonalData().subscribe({
      next: () => {
        UserSettingsOverlayComponent.clearSettingsFromLocalStorage();
        this.userService.logOut();
      },
      error: () => {
        this.backendCallPending = false;
        this.errorMessage = this.translate.instant('user.notificationDeleteError');
      }
    });
  }

  closeDropdown(): void {
    this.state().settingsDialogVisible = false;
  }

  getLabelForUserLocale(locale: string): string {
    switch (locale) {
      // Chinese and Taiwanese should not appear as languages in China, show Simplified/Traditional Chinese instead
      case 'zh-CN':
        return 'zh-SI';
      case 'zh-TW':
        return 'zh-TR';
      default:
        return locale;
    }
  }

  private updateAndPublishUser(): void {
    const user: UserData = this.userService.getCurrentUser();
    user.setUserSettings(this.userSettings);
    this.userService.publishUser(user);
  }

  private setDefaultAppOptions(): void {
    this.defaultAppOptions = this.appsService.getVisibleApps()
      .map((app: IIframeAppConfiguration | IWebComponentAppConfiguration) => ({
        label: this.translate.instant(app.titleResource),
        id: app.titleResource
      }));
  }

  private setEshWriteModeOptions(): void {
    this.eshWriteModeOptions = [
      {label: this.translate.instant('user.eshWriteModeAsk'), id: 'ASK'},
      {label: this.translate.instant('user.eshWriteModeAlways'), id: 'ALWAYS'},
      {label: this.translate.instant('user.eshWriteModeNever'), id: 'NEVER'}
    ];
  }

  protected readonly AddedQuantityStrategy = AddedQuantityStrategy;
}
